/**
 * Demo data, clearly marked "DEMO DATA" on screen, so the hangar screen can be shown without a token:
 * - `/kiosk/demo`: the hangar's own anemometer is reporting, so it is the feed.
 * - `/kiosk/demo-fallback`: the anemometer has gone quiet, so the screen hands over to the field's
 *   airport weather (KFMM, Fort Morgan Municipal) and says so.
 * Times are relative to now, so the ages tick like the real thing.
 */

import { emptyObservation, type Observation } from '@skyweather/weather-core'
import type { Brand, ForecastItem, HourlyPeriod, KioskData, LimitStatus, SourceHealth, WindsAloftItem } from './types.js'

const SITE = 'demo-site'

function obs(sourceId: string, at: number, extra: Partial<Observation>): Observation {
  return { ...emptyObservation(sourceId, at), ...extra }
}

function limit(sourceId: string, sourceLabel: string, setName: string, value: number, state: LimitStatus['state'], lastValue: number, health: SourceHealth, at: number): LimitStatus {
  return {
    siteId: SITE,
    rule: { ruleId: `${setName}-${sourceId}`, setId: setName, setName, metric: 'gustKt', direction: 'above', limit: value, unit: 'kt', enteredValue: value, enteredUnit: 'kt', approachMargin: 3, backWithinMargin: 2, minDurationS: 120 },
    sourceId,
    sourceLabel,
    state,
    since: at - 120_000,
    sourceHealth: health,
    lastValue,
    lastEvidenceAt: at,
    sequence: 3,
  }
}

const svgUrl = (inner: string) => `data:image/svg+xml;base64,${btoa(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 220 48" width="220" height="48">${inner}</svg>`)}`

/** A fictional dropzone's kit, for `/kiosk/demo-brand`. */
export const DEMO_BRAND: Brand = {
  logoLight: svgUrl('<circle cx="24" cy="24" r="18" fill="#2f6fb3"/><path d="M14 28 L24 12 L34 28 Z" fill="#fff"/><text x="50" y="31" font-family="Arial" font-weight="700" font-size="20" fill="#16222e">TESTHARNESS</text>'),
  logoDark: svgUrl('<circle cx="24" cy="24" r="18" fill="#5aa0e6"/><path d="M14 28 L24 12 L34 28 Z" fill="#0e141b"/><text x="50" y="31" font-family="Arial" font-weight="700" font-size="20" fill="#ffffff">TESTHARNESS</text>'),
  primary: '#2f6fb3',
  accent: '#e8a317',
  font: 'Montserrat',
}

export function demoData(now: number, fallback = false, linkDaysLeft = 300, brand: Brand | null = null): KioskData {
  const trend: Observation[] = []
  for (let i = 360; i >= 0; i -= 2) {
    const w = 9 + Math.round((1 - i / 360) * 5 + Math.sin(i / 9) * 1.5)
    trend.push(obs('anemometer', now - i * 60_000, { windKt: w, gustKt: w + 5 + (i % 10 === 0 ? 3 : 0), windDirDeg: 270 }))
  }
  const fieldTrend: Observation[] = []
  for (let i = 360; i >= 0; i -= 20) {
    fieldTrend.push(obs('field', now - i * 60_000 - 5 * 60_000, { windKt: 15 + (i % 40 === 0 ? 3 : 0), gustKt: 23 + (i % 60 === 0 ? 4 : 0), windDirDeg: 160 }))
  }
  const anemometerAt = fallback ? now - 26 * 60_000 : now - 20_000
  const anemometerHealth: SourceHealth = fallback ? 'not_reporting' : 'reporting'
  const fieldAt = now - 6 * 60_000
  return {
    brand,
    site: {
      id: SITE,
      name: 'Skydive Testharness',
      timezone: 'America/Denver',
      units: { wind: 'kt', temp: 'C', pressure: 'inHg' },
      lat: 40.3315,
      lon: -103.807,
      elevationFt: 4514,
      field: { icao: 'KFMM', name: 'Fort Morgan Muni, CO, US' },
      // The demo shows every panel, the two forecast ones included.
      screenFeatures: { windsAloft: true, forecast: true },
      linkExpiresAt: now + linkDaysLeft * 86_400_000 - 3_600_000,
    },
    readings: [
      {
        siteId: SITE,
        descriptor: { id: 'anemometer', siteId: SITE, kind: 'davis-local', label: 'Hangar anemometer', sensorHeightM: 10, expectedIntervalS: 60 },
        header: 'Weather observations (Hangar anemometer)',
        health: anemometerHealth,
        healthSince: fallback ? now - 23 * 60_000 : now - 3 * 3600_000,
        observation: obs('anemometer', anemometerAt, { windowS: 60, windKt: 14, gustKt: 22, lullKt: 9, windDirDeg: 270, tempC: 24, altimeterInHg: 30.01 }),
      },
      {
        siteId: SITE,
        descriptor: { id: 'field', siteId: SITE, kind: 'noaa-metar', label: 'KFMM field weather (AWOS)', expectedIntervalS: 1200 },
        header: 'Weather observations (KFMM field weather (AWOS))',
        health: 'reporting',
        healthSince: now - 6 * 3600_000,
        observation: obs('field', fieldAt, {
          windKt: 16, gustKt: 24, windDirDeg: 160, visibilitySm: 10, ceilingFtAgl: 'none', clouds: [],
          tempC: 28, dewpointC: 4, altimeterInHg: 30.14, precipitation: false,
          raw: 'METAR KFMM 022055Z AUTO 16016G24KT 10SM CLR 28/04 A3014 RMK AO2',
        }),
      },
    ],
    limits: [
      limit('anemometer', 'Hangar anemometer', 'Students', 20, 'past', 22, anemometerHealth, anemometerAt),
      limit('anemometer', 'Hangar anemometer', 'Tandems', 25, 'approaching', 22, anemometerHealth, anemometerAt),
      limit('anemometer', 'Hangar anemometer', 'Licensed', 30, 'within', 22, anemometerHealth, anemometerAt),
      limit('field', 'KFMM field weather (AWOS)', 'Students', 20, 'past', 24, 'reporting', fieldAt),
      limit('field', 'KFMM field weather (AWOS)', 'Tandems', 25, 'approaching', 24, 'reporting', fieldAt),
      limit('field', 'KFMM field weather (AWOS)', 'Licensed', 30, 'within', 24, 'reporting', fieldAt),
    ],
    trends: { anemometer: trend, field: fieldTrend },
    forecast: demoForecast(now),
    windsAloft: demoWindsAloft(now),
  }
}

/** Twelve hours of a made-up NWS forecast from the top of the current hour. */
export function demoForecast(now: number): ForecastItem[] {
  const hour = now - (now % 3600_000)
  const skies = ['Sunny', 'Sunny', 'Mostly Sunny', 'Partly Cloudy', 'Partly Cloudy', 'Chance Showers And Thunderstorms', 'Chance Showers And Thunderstorms', 'Mostly Cloudy', 'Partly Cloudy', 'Clear', 'Clear', 'Clear']
  const periods: HourlyPeriod[] = skies.map((summary, i) => ({
    from: hour + i * 3600_000,
    to: hour + (i + 1) * 3600_000,
    windDirDeg: (160 + i * 15) % 360,
    windKt: 10 + Math.round(Math.sin(i / 2) * 4) + (i > 4 && i < 8 ? 6 : 0),
    windLowKt: null,
    gustKt: i > 4 && i < 8 ? 24 : null,
    tempC: 22 + (i < 6 ? i : 12 - i),
    dewpointC: 5,
    humidityPct: 30,
    precipPct: i > 4 && i < 8 ? 40 : 0,
    summary,
    isDaytime: i < 9,
  }))
  return [{ sourceId: 'nws', kind: 'nws-forecast', label: 'National Weather Service', station: null, issuedAt: now - 25 * 60_000, validFrom: hour, validTo: hour + 12 * 3600_000, periods, raw: null }]
}

/** Denver's winds aloft, roughly as the real bulletin reads for a Front Range afternoon. */
export function demoWindsAloft(now: number): WindsAloftItem[] {
  const from = now - 2 * 3600_000
  const lvl = (altitudeFt: number, windDirDeg: number | null, windKt: number | null, tempC: number | null, raw: string, lightAndVariable = false) => ({ altitudeFt, windDirDeg, windKt, lightAndVariable, tempC, raw })
  return [
    {
      station: 'DEN',
      label: 'Denver (DEN)',
      issuedAt: from - 2 * 3600_000,
      validFrom: from,
      validTo: from + 7 * 3600_000,
      forecastHours: 6,
      levels: [lvl(9000, 110, 8, 9, '1108+09'), lvl(12000, null, null, 3, '9900+03', true), lvl(18000, 350, 20, -9, '3520-09'), lvl(24000, 340, 24, -20, '3424-20')],
    },
  ]
}
